import type { Marking } from './schema'
import { collectMarkableText, findCitationParent, iterateMarkableTextNodes } from './text-filter'

/**
 * Finds `textBefore + text + textAfter` in the root's markable text and
 * returns the Range; falls back to the text alone when context has shifted.
 * Ambiguous matches (either pass) return null — anchoring to the wrong
 * occurrence corrupts the marking. Callers batching lookups pass `unitText`
 * to avoid recollecting the markable text per marking.
 */
export function locateMarkingRange(
  root: Element,
  marking: Pick<Marking, 'text' | 'textBefore' | 'textAfter'>,
  unitText?: string,
): Range | null {
  // Empty text would `indexOf` to 0 — refuse outright.
  if (!marking.text) {
    return null
  }

  const searchableText = unitText ?? collectMarkableText(root)
  const quoteWithContext = marking.textBefore + marking.text + marking.textAfter
  const contextMatches = findAllOccurrences(searchableText, quoteWithContext)

  if (contextMatches.length > 1) {
    return null
  }

  const [contextMatchStart] = contextMatches

  if (contextMatchStart !== undefined) {
    const quoteStart = contextMatchStart + marking.textBefore.length
    return rangeFromMarkableOffsets(root, quoteStart, quoteStart + marking.text.length)
  }

  // Context shifted (surrounding text edited) — fall back to the quote alone.
  const [quoteMatchStart, ambiguousQuoteMatch] = findAllOccurrences(searchableText, marking.text)

  if (quoteMatchStart !== undefined && ambiguousQuoteMatch === undefined) {
    return rangeFromMarkableOffsets(root, quoteMatchStart, quoteMatchStart + marking.text.length)
  }

  return null
}

// Advances by 1 (not phrase.length) so overlapping repeats still count as
// ambiguous; stops at 2 matches — only unique-vs-ambiguous matters.
function findAllOccurrences(text: string, phrase: string): number[] {
  if (!phrase) {
    return []
  }

  const startOffsets: number[] = []
  let searchFrom = 0

  while (searchFrom <= text.length) {
    const found = text.indexOf(phrase, searchFrom)

    if (found < 0) {
      break
    }

    startOffsets.push(found)

    if (startOffsets.length > 1) {
      break
    }

    searchFrom = found + 1
  }

  return startOffsets
}

// Range for [start, end) offsets within collectMarkableText(root) — walks
// only markable text nodes, mirroring the capture-side offset computation.
function rangeFromMarkableOffsets(root: Element, start: number, end: number): Range | null {
  const citationParent = findCitationParent(root) ?? root
  let cursor = 0
  let startNode: Text | null = null
  let startOffset = 0
  let endNode: Text | null = null
  let endOffset = 0

  for (const node of iterateMarkableTextNodes(root, citationParent)) {
    const next = cursor + node.length

    if (!startNode && start <= next) {
      startNode = node
      startOffset = start - cursor
    }

    if (!endNode && end <= next) {
      endNode = node
      endOffset = end - cursor
      break
    }

    cursor = next
  }

  if (!startNode || !endNode) {
    return null
  }

  const range = document.createRange()
  range.setStart(startNode, startOffset)
  range.setEnd(endNode, endOffset)
  return range
}
